<template>
  <v-container fill-height justify-content-start :class="{'pa-2': $vuetify.breakpoint.xsOnly}">
    <v-layout row wrap v-if="!noBaseCurrencyError && !noStationError && accountDetails && accountDetails.account && accountDetails.account.active">
      <v-layout column class="flex-basis-60">
        <v-layout row wrap class="flex-none" v-if="!loadingGuest">
          <v-flex xs12 class="px-1">
            <div xs12 class="px-1 headline mb-2" v-if="profile">
              <span class="font-weight-light mr-3">{{$t('form.guest')}}</span>
              <span class="font-weight-medium">{{(profile.name ? (profile.name + ' ') : ('')) + (profile.lastName ? profile.lastName : '')}}</span>
            </div>
            <v-autocomplete
              color="accent"
              outline
              :filter="profileFilter"
              :label="$t('form.transaction_guest')"
              item-value="id"
              v-model="guestId"
              :items="filteredGuests">
              <template v-slot:selection="{ item }">
                {{(item.profile.name ? (item.profile.name + ' ') : ('')) + (item.profile.lastName ? item.profile.lastName : '')}}
              </template>
              <template v-slot:item="{ item }">
                {{(item.profile.name ? (item.profile.name + ' ') : ('')) + (item.profile.lastName ? item.profile.lastName : '')}}
              </template>
            </v-autocomplete>
            <v-layout row align-center class="body-2 mb-2" v-if="form.discount">
              <v-icon class="warning--text text--lighten-2 mr-2">mdi-sale</v-icon>{{$t('points.discount', { discount: form.discount })}}
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout v-else class="flex-none" column>
          <v-flex xs12 class="px-1 headline">
            <span class="font-weight-light">{{$t('form.guest')}}</span>
          </v-flex>
          <v-flex xs12 class="px-1">
            <v-layout justify-center>
              <v-progress-circular indeterminate color="secondary"/>
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout row wrap class="flex-none mb-2" align-center>
          <v-flex class="px-1 headline flex-none" v-if="pointsCurrency" align-center>
            <span class="caption grey--text">{{$t('form.current_balance')}}</span><br/>
            <span class="font-weight-medium mr-3">{{formatAmount(pointsCurrency.balance)}}</span><span class="font-weight-light title">{{pointsCurrency.currency.name}}</span>
          </v-flex>
          <v-flex class="px-1 headline flex-none" v-if="rechargeAmount">
            <v-layout align-center>
            <div class="mr-3">
              <v-icon>
                mdi-arrow-right
              </v-icon>
            </div>
            <span>
              <span class="caption grey--text">{{$t('form.final_balance')}}</span><br/>
              <span class="font-weight-medium mr-3">{{formatAmount(pointsCurrency.balance + form.pointsAmount)}}</span><span class="font-weight-light title">{{pointsCurrency.currency.name}}</span>
            </span>
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout row wrap class="flex-none">
          <v-flex xs12 class="px-1 display-1">
            <v-text-field color="accent" placeholder="0" class="currency" outline :label="$t('form.amount')" v-model="rechargeAmount" />
          </v-flex>
        </v-layout>
        <v-layout row wrap class="flex-none">
          <v-btn large :outline="selectedTransactionType !== 'BOOKING'" color="secondary" @click="transactionType = 'BOOKING'">
            <span class="margin-right-auto">{{$t('transaction_types.booking')}}</span>
          </v-btn>
          <v-btn large :outline="selectedTransactionType !== 'PAYMENT'" color="secondary" @click="transactionType = 'PAYMENT'">
            <span class="margin-right-auto">{{$t('transaction_types.payment')}}</span>
          </v-btn>
        </v-layout>
        <v-fade-transition>
        <v-layout row wrap class="flex-none pt-2" v-if="selectedTransactionType === 'PAYMENT'">
          <v-flex xs12 class="px-1">
            <v-autocomplete color="accent" outline :label="$t('form.payment_currency')" item-value="id" item-text="name" v-model="paymentCurrencyId" :items="allowedCurrencies"/>
          </v-flex>
          <v-flex xs12 class="px-1">
            <v-autocomplete color="accent" outline :label="$t('form.payment_method')" item-value="id" item-text="name" v-model="paymentMethodId" :items="allowedPaymentMethods"/>
          </v-flex>
        </v-layout>
        </v-fade-transition>
      </v-layout>
      <v-layout column class="flex-basis-40">
        <v-spacer/>
        <v-layout row wrap justify-end align-center class="flex-none" v-if="displayPrice">
          <v-flex class="px-1 flex-none" :class="{'headline': !displayConvertedPrice, 'title': displayConvertedPrice}" v-if="pointsCurrency" align-center>
            <span class="caption grey--text">{{$t('form.price')}}</span><br/>
            <span class="font-weight-medium mr-3" :class="{'strikethrough': !!discountedPrice}">
              {{formatAmount(displayPrice)}}
            </span>
            <span v-if="discountedPrice" class="font-weight-medium mr-3">
              <v-layout row>
              {{formatAmount(discountedPrice)}}
              <v-icon class="grey--text text--lighten-1">mdi-sale</v-icon>
              </v-layout>
            </span>
            <span class="font-weight-light" :class="{'title': !displayConvertedPrice, 'subheading': displayConvertedPrice}">{{facility.baseCurrency.name}}</span>
          </v-flex>
          <v-flex class="px-1 headline flex-none" v-if="paymentCurrency && paymentCurrency.id !== facility.baseCurrencyId && paymentCurrency.exchangeRate">
            <v-layout align-center>
            <div class="mr-3">
              <v-icon>
                mdi-arrow-right
              </v-icon>
            </div>
            <span>
              <span class="caption grey--text">{{$t('form.price_conversion')}}</span><br/>
              <span class="font-weight-medium mr-3">{{formatAmount(displayConvertedPrice)}}</span><span class="font-weight-light title">{{paymentCurrency.name}}</span>
            </span>
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout row wrap justify-end class="flex-none">
          <v-flex class="px-1 headline flex-none" v-if="paymentCurrency && paymentCurrency.id !== facility.baseCurrencyId && !paymentCurrency.exchangeRate">
            <v-alert class="py-2 alert__no-border mx-0" type="warning" :value="true">{{$t('warnings.no_exchange_rate')}}</v-alert>
          </v-flex>
          <v-btn :loading="loading" color="primary" @click="submit()" :disabled="!validForm">
            {{displayAmount
              ? $t('actions.purchase_points', { amount: formatAmount(displayAmount), currency: pointsCurrency.currency.name })
              : $t('actions.purchase_points', { amount: null, currency: null })}}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-layout>
    <v-layout
      justify-center
      align-center
      column
      class="display-1 grey--text"
      v-else-if="accountDetails && accountDetails.account && !accountDetails.account.active"
    >
      <v-icon style="font-size: 100px;">mdi-close-circle-outline</v-icon>
      <div>
      {{$t('points.disabled_account')}}</div>
    </v-layout>
    <v-layout
      justify-center
      align-center
      column
      class="display-1 grey--text"
      v-else-if="noStationError"
    >
      <v-icon style="font-size: 100px;">store</v-icon>
      <div class="text-xs-center">
      {{$t('points.no_station')}}</div>
    </v-layout>
    <v-layout
      justify-center
      align-center
      column
      class="display-1 grey--text"
      v-else-if="noBaseCurrencyError"
    >
      <v-icon style="font-size: 100px;">store</v-icon>
      <div class="text-xs-center">
      {{$t('points.no_base_currency_allowed')}}</div>
    </v-layout>
    <v-layout
      justify-center
      align-center
      column
      class="display-1 grey--text"
      v-else-if="noPointsCurrencyError"
    >
      <v-icon style="font-size: 100px;">store</v-icon>
      <div class="text-xs-center">
      {{$t('points.no_points_currency_allowed')}}</div>
    </v-layout>
    <v-layout justify-center align-center v-else>
      <v-progress-circular indeterminate color="secondary"/>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import moneyFormatMixins from '@/mixins/MoneyFormat'
import searchMixins from '@/mixins/Search'
import { POINTS_CURRENCY_ID, PANEL_LOCATION } from '@/config/environment'
export default {
  name: 'points',
  mixins: [
    moneyFormatMixins,
    searchMixins
  ],
  data () {
    return {
      displayAmount: null,
      displayPrice: null,
      displayConvertedPrice: null,
      selectedTransactionType: null,
      paymentCurrency: null,
      loading: false,
      loadingGuest: false,
      noStationError: false,
      noBaseCurrencyError: false,
      noPointsCurrencyError: false
    }
  },
  computed: {
    ...mapState(['form', 'currencies', 'accountDetails', 'guest', 'booking', 'facility', 'paymentMethods', 'station']),
    validForm () {
      if (!this.form.pointsAmount) {
        return false
      }
      if (!this.form.paymentCurrencyId) {
        return false
      }
      if (this.transactionType === 'PAYMENT') {
        if (!this.form.paymentMethodId) {
          return false
        }
        if ((this.paymentCurrencyId !== this.facility.baseCurrencyId) && (!this.paymentCurrency || (!this.paymentCurrency.exchangeRate))) {
          return false
        }
        return true
      } else if (this.transactionType === 'BOOKING') {
        return true
      }
      return false
    },
    filteredGuests () {
      return this.booking ? this.booking.guests.filter((g) => {
        return g.accountId === this.$route.query.account_id
      }) : []
    },
    allowedCurrencies () {
      return this.currencies.filter(c => c.type === 'LEGAL' && this.station.allowedCurrencyIds.indexOf(c.id) >= 0)
    },
    allowedPaymentMethods () {
      return this.paymentMethods ? this.paymentMethods.filter(p => this.station.allowedPaymentMethodIds.indexOf(p.id) >= 0) : []
    },
    guestId: {
      get () {
        return this.form ? this.form.guestId : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'guestId', value })
      }
    },
    paymentCurrencyId: {
      get () {
        return this.form ? this.form.paymentCurrencyId : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'paymentCurrencyId', value })
        if (value !== this.facility.baseCurrencyId) {
          this.$store.dispatch('getExchangeRate', value).then((currency) => {
            this.paymentCurrency = currency
            this.$store.commit('setField', { field: 'paymentCurrency', value: this.paymentCurrency })
            this.getPrice(this.form.pointsAmount)
          })
        } else {
          this.paymentCurrency = null
          this.$store.commit('setField', { field: 'paymentCurrency', value: this.paymentCurrency })
        }
      }
    },
    paymentMethodId: {
      get () {
        return this.form ? this.form.paymentMethodId : null
      },
      set (value) {
        this.$store.commit('setField', { field: 'paymentMethodId', value })
      }
    },
    rechargeAmount: {
      get () {
        return this.displayAmount
      },
      set (value) {
        if (typeof value !== 'undefined' && value !== '') {
          this.setAmount(value)
        } else {
          this.displayAmount = null
          this.$store.commit('setField', {
            field: 'pointsAmount',
            value: null
          })
        }
      }
    },
    transactionType: {
      get () {
        return this.selectedTransactionType
      },
      set (value) {
        this.selectedTransactionType = value
        if (this.selectedTransactionType === 'PAYMENT') {
          this.$store.dispatch('listPaymentMethods').catch(() => {})
        }
        if (this.paymentCurrencyId !== this.facility.baseCurrencyId) {
          this.paymentCurrencyId = this.facility.baseCurrencyId
        }
        this.$store.commit('setField', {
          field: 'transactionType',
          value
        })
      }
    },
    profile () {
      return this.accountDetails ? this.accountDetails.account.profile : undefined
    },
    pointsCurrency () {
      var currency = this.accountDetails ? this.accountDetails.currencies.find((c) => { return c.currency.id === POINTS_CURRENCY_ID }) : null
      return currency
    },
    defaultCurrency () {
      var currency = this.accountDetails ? this.accountDetails.currencies.find((c) => { return c.currency.id === this.facility.baseCurrencyId }) : null
      return currency
    },
    discountedPrice () {
      return this.form.price && this.form.discount
        ? this.form.price - (this.form.price * this.form.discount / 100)
        : null
    }
  },
  async created () {
    this.loadingGuest = true
    this.$store.commit('setToken', this.$route.params.token)
    if (this.$route.query.station_id && this.$route.query.station_id !== 'null') {
      this.$store.commit('setField', {
        field: 'stationId',
        value: this.$route.query.station_id
      })
      await this.$store.dispatch('getStation', { id: this.$route.query.station_id })
      if (this.station.allowedCurrencyIds.indexOf(POINTS_CURRENCY_ID) < 0) {
        this.noPointsCurrencyError = true
        return
      }
    } else {
      this.noStationError = true
      return
    }
    await this.$store.dispatch('getAccount', this.$route.query.account_id)
    if (this.accountDetails.account.active) {
      await this.$store.dispatch('listCurrencies').then(() => {
        this.applyStyle(this.generateCurrencyStyleDef())
      })
      if (this.$route.query.booking_id && this.$route.query.booking_id !== 'null') {
        await this.$store.dispatch('getBooking', this.$route.query.booking_id).then((booking) => {
          var foundGuest = this.$route.query.guest_id
          if (!foundGuest && booking.guests.length) {
            var guest = booking.guests.find((g) => {
              return this.accountDetails.account.profile.id === g.profileId
            })
            if (guest) {
              foundGuest = guest.id
            }
          }
          this.$store.commit('setField', {
            field: 'guestId',
            value: foundGuest
          })
        })
      }
      if (this.$route.query.guest_id) {
        await this.$store.dispatch('getGuest', { guestId: this.$route.query.guest_id, getFacility: !this.$route.query.booking_id || this.$route.query.booking_id === 'null' })
      }
      if (this.station.allowedCurrencyIds.indexOf(this.facility.baseCurrencyId) < 0) {
        this.noBaseCurrencyError = true
      }
      this.loadingGuest = false
      this.$store.dispatch('listCurrencies').then(() => {
        this.applyStyle(this.generateCurrencyStyleDef())
      })
      this.$store.dispatch('getExchangeRate', POINTS_CURRENCY_ID).then((currency) => {
        this.pointsCurrency.currency = currency
      })
    }
  },
  watch: {
    displayConvertedPrice (value) {
      this.$store.commit('setField', {
        field: 'convertedPrice',
        value
      })
    },
    guestId (value) {
      var guest = this.booking.guests.find((g) => { return g.id === value })
      this.$store.commit('setField', {
        field: 'discount',
        value: null
      })
      this.$store.dispatch('getProfile', { profileId: guest.profileId }).then(async (profile) => {
        if (profile.categoryId) {
          await this.$store.dispatch('getProfileCategory', { id: profile.categoryId }).then((category) => {
            if (category.attributes.discount) {
              this.$store.commit('setField', {
                field: 'discount',
                value: parseInt(category.attributes.discount)
              })
            }
          })
        }
        this.getPrice(this.form.pointsAmount)
      })
    }
  },
  methods: {
    profileFilter (item, queryText, itemText) {
      var text = `${item.profile.name} ${item.profile.lastName} ${item.profile.name}`
      queryText = this.sanitize(queryText)
      return this.sanitize(text).indexOf(queryText) !== -1
    },
    getPrice (val) {
      this.displayPrice = this.pointsCurrency.currency.exchangeRate ? val * this.pointsCurrency.currency.exchangeRate.exchangeRate : val
      this.$store.commit('setField', {
        field: 'price',
        value: this.displayPrice
      })
      if (this.paymentCurrency && (this.paymentCurrencyId !== this.facility.baseCurrencyId)) {
        if (this.discountedPrice) {
          this.displayConvertedPrice = (this.paymentCurrency && this.paymentCurrency.exchangeRate)
            ? this.discountedPrice / this.paymentCurrency.exchangeRate.exchangeRate
            : null
        } else {
          this.displayConvertedPrice = (this.paymentCurrency && this.paymentCurrency.exchangeRate)
            ? this.displayPrice / this.paymentCurrency.exchangeRate.exchangeRate
            : null
        }
      } else {
        this.displayConvertedPrice = null
      }
    },
    submit () {
      this.loading = true
      this.$store.dispatch('purchasePoints').then(() => {
        if (window.opener) {
          window.opener.postMessage('refreshData', PANEL_LOCATION)
          window.close()
        }
        this.$store.commit('setMessage', { text: this.$t('message.complete_main'), color: 'success', timeout: 0 })
      }).catch(() => {
        this.loading = false
      })
    },
    setAmount (newValue) {
      this.displayAmount = ''
      this.$nextTick(() => {
        this.displayAmount = this.sanitizePrice(newValue)
        this.$store.commit('setField', {
          field: 'pointsAmount',
          value: this.parsePrice(newValue)
        })
        this.getPrice(this.parsePrice(newValue))
      })
    },
    applyStyle (styleDef) {
      let style = document.createElement('style')
      style.type = 'text/css'
      document.head.appendChild(style)
      style.innerHTML = styleDef
    },
    generateCurrencyStyleDef () {
      return `
        .v-text-field.currency .v-text-field__slot::after {
          content: '${this.pointsCurrency ? this.pointsCurrency.currency.name : ''}' !important;
          margin-top: auto !important;
          margin-left: 5px;
        }
        :not(.v-text-field).currency::after {
          content: '${this.pointsCurrency ? this.pointsCurrency.currency.name : ''}' !important;
        }
      `
    }
  }
}
</script>

<style lang="scss">
.v-text-field.currency .v-text-field__slot > input {
  text-align: right;
  margin-right: 5px;
  font-size: 30px;
  max-height: 40px;
}
.flex-basis-60 {
  flex-basis: 60%;
}
.flex-basis-40 {
  flex-basis: 40%;
}
.flex-basis-50 {
  flex-basis: 50%;
}
.alert__no-border {
  border-top: none !important;
}
</style>
